import {emptyStringIfNil} from '../privates/utils';
import * as members from '../privates/privateMembers';
import imageUtils from '../privates/utils/imageUtils';
import {validate} from '../privates/validations/validation';
import {IMAGE_FIT_MODE_MAP, IMAGE_FIT_MODE_INVERTED_MAP} from '../privates/constants';
import BaseImage from './BaseImage';

/**
 * @class Image
 * @summary Images are images stored in the [Media Manager](https://support.wix.com/en/article/about-the-media-manager)
 *  or retrieved from an external web location.
 * @description
 *  The URL formats for images are:
 *
 *  + Images from the Media Manager: `wix:image://v1/<uri>/<filename>#originWidth=<width>&originHeight=<height>[&watermark=<watermark_manifest_string>]`
 *  + Images from the web: `http(s)://<image url>`
 * @memberof $w
 * @queryable
 * @mixes $w.Element
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.LinkableMixin
 * @mixes $w.ClickableMixin
 * @viewername wysiwyg.viewer.components.WPhoto
 * @definitionId wysiwyg.viewer.components.WPhoto
 * @tagname image
 */
class Image extends BaseImage {
    /**
     * @member alt
     * @label property
     * @summary Sets or gets an image's alt text.
     * @description
     *  An image's alternative text is typically a relatively short phrase that
     *  describes what the image depicts.
     *
     *  The alternative text is used:
     *
     *  + If the browser cannot display the image.
     *  + If the user is utilizing a screen reader.
     *  + By search engines to understand what images are on your site.
     * @snippet [Image-alt_get.es6=Get the image's alt text]
     * @snippet [Image-alt_set.es6=Set the image's alt text]
     * @type {String}
     * @default An empty string
     * @memberof $w.Image
     * @instance
     * @attr {String}
     */
    get alt() {
        const RMI = members._RMI.get(this);
        return RMI.getData(members._compId.get(this)).alt;
    }

    validateTooltip(value) {
        const compType = members._type.get(this).compType;
        const isValid = validate({
            compType,
            functionName: 'tooltip',
            dataToValidate: {tooltip: value},
            compName: this.id
        });
        return isValid;
    }
    /**
     * @member alt
     * @memberof $w.Image
     * @param {String} value
     */
    set alt(value) {
        if(!this.validateAlt(value)) {
            return;
        }
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        RMI.setData(compId, {
            alt: emptyStringIfNil(value)
        });
    }

    /**
     * @member tooltip
     * @label property
     * @summary Sets or gets an image's tooltip.
     * @description
     *  An image's tooltip text is displayed when a user hovers over the image
     *  or when the image opens in a popup.
     * > **Note:** `tooltip` is not supported in Editor X. [Learn more](https://support.wix.com/en/article/editor-x-adding-and-editing-images-on-your-site) about Editor X and images.
     * @snippet [Image-tooltip_get.es6=Get the image's tooltip text]
     * @snippet [Image-tooltip_set.es6=Set the image's tooltip text]
     * @type {String}
     * @default An empty string
     * @memberof $w.Image
     * @instance
     * @attr {String}
     */
    get tooltip() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        return RMI.getData(compId).title;
    }

  /**
   * @member tooltip
   * @memberof $w.Image
   * @param {String} value
   */
    set tooltip(value) {
        if(!this.validateTooltip(value)) {
            return;
        }

        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const title = emptyStringIfNil(value);
        RMI.setData(compId, {title});
    }

    /**
     * @member src
     * @label property
     * @summary Sets or gets the file location of the image.
     * @description
     *  Setting the `src` property changes the displayed image to
     *  the image found at the new `src` value.
     *
     *  Getting the `src` property returns the location of the
     *  current image file.
     *
     *  The image file can either be an image file from the [Media Manager](https://support.wix.com/en/article/about-the-media-manager)
     *  or an external file from any web location.
     *
     *  The URL formats supported are:
     *
     *  + Images from the Media Manager: `wix:image://v1/<uri>/<filename>#originWidth=<width>&originHeight=<height>[&watermark=<watermark_manifest_string>]`
     *  + Images from the web: `http(s)://<image url>`
     * @snippet [Image-src_get.es6=Get the image's location]
     * @snippet [Image-src_set_wix.es6=Set the image to be an image from the Media Manager]
     * @snippet [Image-src_set_web.es6=Set the image to be an image from the web]
     * @type {String}
     * @default An empty string
     * @memberof $w.Image
     * @instance
     * @attr {String}
     */
    get src() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compData = RMI.getData(compId) || {};

        return imageUtils.buildFullUrlFromUri(compData.uri, compData.width, compData.height, compData.title);
    }

    /**
     * @member src
     * @memberof $w.Image
     * @param {String} value
     */
    set src(value) {
       const imageData = this.prepareSrc(value);
       if(!imageData) {
           return;
       }

       const RMI = members._RMI.get(this);
       const compId = members._compId.get(this);
       RMI.setData(compId, imageData);
    }

    /**
     * @member clickAction
     * @label property
     * @summary Sets or gets the action that occurs when an image is clicked.
     * @description
     *  Setting the `clickAction` property sets what happens when
     *  an image is clicked.
     *
     *  The value can be set to:
     *
     *  + `"none"`: Nothing happens.
     *  + `"expand"`: The image opens in a popup window.
     *  + `"link"`: The image's [`link`](#link) opens.
     *  + `"magnified"`: The cursor becomes a magnifying glass when a visitor hovers over the image.
     *
     *
     *  Getting the `clickAction` property returns what happens when
     *  an image is clicked.
     * > **Note:** `expand ` and `magnified` are not supported in Editor X. [Learn more](https://support.wix.com/en/article/editor-x-adding-and-editing-images-on-your-site) about Editor X and images.
     * @snippet [Image-clickAction_get.es6=Get the action that occurs when an image is clicked]
     * @snippet [Image-clickAction_set.es6=Set the action that occurs when an image is clicked]
     * @type {String}
     * @memberof $w.Image
     * @instance
     */
    get clickAction() {
        return this.getClickAction();
    }

    /**
     * @member clickAction
     * @memberof $w.Image
     * @param {String} action
     */
    set clickAction(action) {
        this.setClickAction(action);
    }

    /**
     * @member fitMode
     * @label property
     * @summary Sets or gets how an image is placed inside an image element.
     * @description
     *  Setting the `fitMode` property sets what happens when an image element
     *  contains an image with dimensions that do not match the dimensions
     *  of the element as set in the Editor.
     *
     *  The value can be set to one of `"fill"`, `"fit"`, or `"fixedWidth"`.
     * 
     *  The descriptions below contain sample scenarios that place the following images
     *  into an image element, where the size of the image element is 100x100.
     *
     *  ![fitMode example images](images/fitMode_images.png "fitMode example images")
     *
     *  + `"fill"`: Fills the entire image element. The image is resized (meaning
     *    enlarged or reduced), maintaining its original aspect ratio, so that there
     *    is no empty area in the image element. When an image is resized, the center
     *    of the original image is shown in the image element and the part of the original
     *    image that does not fit into the image element is not shown.
     *
     *    ![fitMode fill example images](images/fitMode-fill.png "fitMode fill example images")
     *
     *  + `"fit"`: Fits the entire image in the image element. If the image is reduced,
     *    the image is centered in the image element and any empty space is filled in
     *    with the image element's border color and opacity.
     *
     *    ![fitMode fit example images](images/fitMode-fit.png "fitMode fit example images")
     *
     *  + `"fixedWidth"`: The image element's width remains fixed and the image is enlarged
     *    or reduced, maintaining its original aspect ratio, so that it fits the image
     *    element's width. The image element's height is adjusted accordingly.
     *
     *    ![fitMode fixed width example images](images/fitMode-fixedWidth.png "fitMode fixed width example images")
     *
     * 
     *  Getting the `fitMode` property returns the current fit mode.
     * > **Note:** `fitMode` is not supported in Editor X. [Learn more](https://support.wix.com/en/article/editor-x-adding-and-editing-images-on-your-site) about Editor X and images.
     * @snippet [Image-fitMode_get.es6=Get the image fit mode]
     * @snippet [Image-fitMode_set.es6=Set the image fit mode]
     * @default "fill"
     * @type {String}
     * @memberof $w.Image
     * @instance
     */
    get fitMode() {
        const RMI = members._RMI.get(this);
        const displayMode = RMI.getProps(members._compId.get(this)).displayMode;
        return IMAGE_FIT_MODE_INVERTED_MAP[displayMode];
    }

    /**
     * @member fitMode
     * @memberof $w.Image
     * @param {String} mode
     */
    set fitMode(value) {
        const compType = members._type.get(this).compType;
        const isValid = validate({
            compType,
            functionName: 'fitMode',
            dataToValidate: {fitMode: String(value)},
            compName: this.id
        });
        if (!isValid) {
            return;
        }

        const RMI = members._RMI.get(this);
        return RMI.setProps(members._compId.get(this), {displayMode: IMAGE_FIT_MODE_MAP[value]});
    }

    toJSON() {
        const {id, type, global, rendered, alt, tooltip, src} = this;
        return {id, type, global, rendered, alt, tooltip, src};
    }
}

export default Image;
